import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, ArrowRight, Bot, Eye, FileText, AlertTriangle, CheckCircle, XCircle, Shield, Video, Mic, Image } from 'lucide-react';
import SEO from '../SEO';
import '../SeoPages.css';

function AIContentGuidelinesGuide() {
  return (
    <>
      <SEO
        title="AI-Generated Content Guidelines: YouTube, TikTok & Instagram Rules (2026)"
        description="Complete guide to AI content policies on YouTube, TikTok, and Instagram. Learn disclosure requirements, what's allowed, and how to avoid strikes with AI-generated videos."
        keywords="AI content guidelines, AI generated video rules, YouTube AI policy, TikTok AI content, synthetic media disclosure, deepfake rules, AI avatar policy, AI voice content"
        url="/guides/ai-content-guidelines"
      />
      <div className="seo-page">
        <div className="seo-container">
          {/* Header */}
          <header className="seo-header">
            <Link to="/" className="seo-back-link">
              <ArrowLeft size={20} />
              <span>Back to Home</span>
            </Link>
            <Link to="/" className="seo-brand">
              <div className="seo-logo-circle">
                <Sparkles className="seo-sparkles-icon" />
              </div>
              <span className="seo-brand-name">MyCleanStudio</span>
            </Link>
          </header>

          {/* Hero */}
          <div className="seo-hero">
            <h1>AI-Generated Content Guidelines for Creators</h1>
            <p className="seo-hero-subtitle">
              Understand the new rules for AI content on YouTube, TikTok, and Instagram to stay compliant and avoid strikes.
            </p>
          </div>

          {/* Content */}
          <main className="seo-content">
            <div className="seo-toc">
              <h3>In This Guide</h3>
              <ul>
                <li><a href="#overview">AI Content Landscape in 2026</a></li>
                <li><a href="#youtube-ai">YouTube AI Policies</a></li>
                <li><a href="#tiktok-ai">TikTok AI Policies</a></li>
                <li><a href="#instagram-ai">Instagram AI Policies</a></li>
                <li><a href="#disclosure">Disclosure Requirements</a></li>
                <li><a href="#prohibited">What's Prohibited</a></li>
                <li><a href="#best-practices">Best Practices</a></li>
              </ul>
            </div>

            <section id="overview">
              <h2>AI Content Landscape in 2026</h2>
              <p>
                AI-generated content has exploded across social media - from AI avatars and voice cloning to fully synthetic videos. Platforms have responded with new policies that every creator needs to understand.
              </p>
              <p>
                The key principle across all platforms: <strong>transparency</strong>. Viewers have a right to know when content is AI-generated, especially when it could be mistaken for real footage.
              </p>

              <div className="seo-problem-grid">
                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon purple">
                    <Video size={20} />
                  </div>
                  <h4>AI-Generated Video</h4>
                  <p>Synthetic video created by AI tools like Sora, Runway, or Pika. Must be disclosed on most platforms.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon blue">
                    <Mic size={20} />
                  </div>
                  <h4>AI Voice Cloning</h4>
                  <p>Using AI to replicate someone's voice. Heavily regulated, especially for public figures.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon yellow">
                    <Bot size={20} />
                  </div>
                  <h4>AI Avatars</h4>
                  <p>Digital presenters and AI-generated faces. Generally allowed but may require disclosure.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon green">
                    <Image size={20} />
                  </div>
                  <h4>AI-Enhanced Content</h4>
                  <p>Traditional content enhanced with AI (filters, editing). Usually doesn't require disclosure.</p>
                </div>
              </div>

              <div className="seo-warning-box">
                <p>
                  <strong>Important:</strong> AI policies are evolving rapidly. What's allowed today may require disclosure tomorrow. Check platform guidelines regularly and when in doubt, disclose.
                </p>
              </div>
            </section>

            <section id="youtube-ai">
              <h2>YouTube AI Policies</h2>
              <p>
                YouTube introduced comprehensive AI disclosure requirements in 2024, with updates in 2025 and 2026. Here's what you need to know:
              </p>

              <h3>Mandatory Disclosure</h3>
              <p>You <strong>must</strong> disclose AI content that:</p>
              <ul>
                <li>Makes a real person appear to say or do something they didn't</li>
                <li>Alters footage of real events or places</li>
                <li>Generates realistic scenes that could be mistaken for real</li>
                <li>Uses AI-generated voices that sound like real people</li>
              </ul>

              <h3>How to Disclose on YouTube</h3>
              <ul>
                <li>Use YouTube Studio's <strong>"Altered content"</strong> label in video details</li>
                <li>YouTube adds a label visible to viewers in the description area</li>
                <li>For sensitive topics (health, elections, news), labels appear more prominently</li>
              </ul>

              <div className="seo-feature-list">
                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <CheckCircle size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>Doesn't Require Disclosure</h4>
                    <p>AI for color correction, lighting, filters, beauty effects, background blur, or clearly unrealistic content (obvious animation, sci-fi scenes).</p>
                  </div>
                </div>

                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <AlertTriangle size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>Requires Disclosure</h4>
                    <p>AI-generated realistic faces, synthetic voices resembling real people, altered footage of real events, deepfakes of any kind.</p>
                  </div>
                </div>
              </div>

              <h3>Penalties for Non-Disclosure</h3>
              <ul>
                <li>Video removal</li>
                <li>Channel strikes</li>
                <li>Suspension from YouTube Partner Program</li>
                <li>Potential account termination for repeated violations</li>
              </ul>
            </section>

            <section id="tiktok-ai">
              <h2>TikTok AI Policies</h2>
              <p>
                TikTok requires labeling AI-generated content (AIGC) and has strict rules about synthetic media:
              </p>

              <h3>TikTok's AIGC Label</h3>
              <p>TikTok automatically detects and labels some AI content, but creators should also:</p>
              <ul>
                <li>Use the <strong>"AI-generated"</strong> content toggle when uploading</li>
                <li>Add disclosure in the caption for AI avatars or synthetic media</li>
                <li>Never claim AI content as real footage</li>
              </ul>

              <h3>Prohibited AI Content on TikTok</h3>
              <div className="seo-problem-grid">
                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon red">
                    <XCircle size={20} />
                  </div>
                  <h4>Deepfakes of Real People</h4>
                  <p>AI content showing real people in misleading or harmful scenarios without consent.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon red">
                    <XCircle size={20} />
                  </div>
                  <h4>Election Misinformation</h4>
                  <p>AI content about elections, candidates, or voting that could mislead viewers.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon red">
                    <XCircle size={20} />
                  </div>
                  <h4>Fake News/Events</h4>
                  <p>Synthetic media presented as real news footage or documentation of events.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon red">
                    <XCircle size={20} />
                  </div>
                  <h4>AI Voices of Public Figures</h4>
                  <p>Using AI to clone voices of politicians, celebrities, or public figures without authorization.</p>
                </div>
              </div>

              <div className="seo-highlight-box">
                <p>
                  <strong>TikTok's AI Effects:</strong> Using TikTok's built-in AI effects and filters is fine and doesn't require additional disclosure - TikTok handles this automatically.
                </p>
              </div>
            </section>

            <section id="instagram-ai">
              <h2>Instagram AI Policies</h2>
              <p>
                Instagram (Meta) has implemented AI content labeling across all its platforms:
              </p>

              <h3>Automatic Detection</h3>
              <p>
                Instagram uses technology to detect AI-generated images and videos. When detected, content is automatically labeled "Made with AI."
              </p>

              <h3>Self-Disclosure</h3>
              <ul>
                <li>When posting, use the <strong>"AI-generated"</strong> disclosure option</li>
                <li>For Reels, indicate AI content during the upload process</li>
                <li>Include disclosure in captions when AI significantly alters reality</li>
              </ul>

              <h3>What Instagram Allows</h3>
              <ul>
                <li>AI-generated art and creative content (with labels)</li>
                <li>AI avatars and virtual influencers (must be disclosed)</li>
                <li>AI-enhanced photos (filters, editing)</li>
                <li>AI-assisted content creation tools</li>
              </ul>

              <h3>What Instagram Prohibits</h3>
              <ul>
                <li>Undisclosed deepfakes or manipulated media</li>
                <li>AI content designed to deceive or mislead</li>
                <li>Fake endorsements using AI-generated likenesses</li>
                <li>AI-generated intimate imagery without consent</li>
              </ul>
            </section>

            <section id="disclosure">
              <h2>Disclosure Requirements Summary</h2>
              <p>
                Here's a quick reference for when you need to disclose AI content:
              </p>

              <h3>Always Disclose</h3>
              <div className="seo-problem-grid">
                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon yellow">
                    <AlertTriangle size={20} />
                  </div>
                  <h4>Realistic Synthetic People</h4>
                  <p>AI-generated faces or bodies that look like real humans.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon yellow">
                    <AlertTriangle size={20} />
                  </div>
                  <h4>Altered Real People</h4>
                  <p>Any manipulation of real people's appearance, voice, or actions.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon yellow">
                    <AlertTriangle size={20} />
                  </div>
                  <h4>Fake Events/Footage</h4>
                  <p>AI-generated scenes that could be mistaken for real events.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon yellow">
                    <AlertTriangle size={20} />
                  </div>
                  <h4>Cloned Voices</h4>
                  <p>AI voices trained on or imitating real people.</p>
                </div>
              </div>

              <h3>No Disclosure Needed</h3>
              <ul>
                <li>AI color grading, lighting adjustments, noise reduction</li>
                <li>AI background removal or blur</li>
                <li>AI-powered editing tools (cuts, transitions)</li>
                <li>AI subtitles or transcription</li>
                <li>Obviously fictional or animated content</li>
                <li>AI writing assistance for scripts or descriptions</li>
              </ul>
            </section>

            <section id="prohibited">
              <h2>Universally Prohibited AI Content</h2>
              <p>
                Regardless of disclosure, some AI content is banned across all platforms:
              </p>

              <ul>
                <li><strong>Non-consensual intimate imagery</strong> - AI-generated sexual content of real people</li>
                <li><strong>Child exploitation</strong> - Any AI-generated content involving minors inappropriately</li>
                <li><strong>Fraud and scams</strong> - Using AI to impersonate people for financial gain</li>
                <li><strong>Election interference</strong> - Fake content about voting, candidates, or election processes</li>
                <li><strong>Dangerous misinformation</strong> - AI health advice, fake emergencies, or crisis misinformation</li>
                <li><strong>Identity theft</strong> - Using AI to steal or replicate someone's identity</li>
              </ul>

              <div className="seo-warning-box">
                <p>
                  <strong>Legal Consequences:</strong> Beyond platform bans, creating certain types of AI content (especially deepfakes and non-consensual imagery) can result in criminal charges in many jurisdictions.
                </p>
              </div>
            </section>

            <section id="best-practices">
              <h2>Best Practices for AI Content Creators</h2>
              <p>
                Follow these guidelines to stay safe while creating AI-powered content:
              </p>

              <div className="seo-feature-list">
                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <Eye size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>When in Doubt, Disclose</h4>
                    <p>Over-disclosure is always safer than under-disclosure. If you're unsure whether something needs a label, add one anyway.</p>
                  </div>
                </div>

                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <FileText size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>Keep Records</h4>
                    <p>Document your AI tool usage and content creation process. This helps if you need to appeal a mistaken strike.</p>
                  </div>
                </div>

                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <Shield size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>Get Consent</h4>
                    <p>Before using someone's likeness or voice for AI content, get explicit written permission - even for friends and family.</p>
                  </div>
                </div>

                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <Bot size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>Use Platform Tools</h4>
                    <p>Each platform's built-in AI effects are pre-approved. When possible, use these instead of third-party tools.</p>
                  </div>
                </div>
              </div>

              <h3>Content Checklist Before Posting</h3>
              <ol>
                <li>Does this content show or sound like a real person? → Get consent or use disclosure</li>
                <li>Could viewers mistake this for real footage? → Use disclosure</li>
                <li>Does this involve sensitive topics (news, health, elections)? → Extra scrutiny required</li>
                <li>Have I used the platform's disclosure tools? → Check before publishing</li>
                <li>Would I be comfortable if viewers knew this was AI? → If not, reconsider posting</li>
              </ol>

              <div className="seo-highlight-box">
                <p>
                  MyCleanStudio can analyze your AI-generated content for potential policy violations before you post, helping you identify disclosure requirements and risky content.
                </p>
              </div>
            </section>

            {/* CTA */}
            <div className="seo-cta">
              <h2>Check Your AI Content Before Posting</h2>
              <p>Scan your videos for guideline violations and get compliance recommendations.</p>
              <Link to="/dashboard/new-check" className="seo-cta-button">
                Analyze Your Content
                <ArrowRight size={18} />
              </Link>
            </div>
          </main>

          {/* Footer */}
          <footer className="seo-footer">
            <div className="seo-footer-links">
              <Link to="/guides/youtube-guidelines">YouTube Guidelines</Link>
              <Link to="/guides/tiktok-guidelines">TikTok Guidelines</Link>
              <Link to="/guides/sponsored-content-ftc">FTC Disclosure Guide</Link>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/terms">Terms of Service</Link>
            </div>
            <p>&copy; {new Date().getFullYear()} MyCleanStudio. All rights reserved.</p>
          </footer>
        </div>
      </div>
    </>
  );
}

export default AIContentGuidelinesGuide;
